import { useEffect, useState } from 'react';
import { logger } from '../lib/logger';
import { useAlloy } from './use-alloy';
import { decodeKndctrCookie } from '../lib/dynamicContentAssemblyUtils/decodeKndctrCookie';
import { getAepCookieName, isV2UxlEnabled } from '../lib/helpers';
import Cookies from 'js-cookie';

export type IdentityResponse = { identity: { ECID: string }; edge: { regionId: number } };

// Public hook – will early return if v2 is not enabled
export function useIdentity() {
  const isV2Uxl = isV2UxlEnabled();
  return isV2Uxl ? useIdentityInternal() : { identity: undefined };
}

// Internal logic runs only when v2 is active
function useIdentityInternal() {
  const { log } = logger({})('useIdentity');
  const { alloy } = useAlloy();
  const [identity, setIdentity] = useState<{ identity?: string }>();

  useEffect(() => {
    // if state is already set, return
    if (identity) {
      return;
    }

    (async () => {
      const storedIdentity = decodeKndctrCookie();
      const clusterCookieName = getAepCookieName('cluster');
      const clusterCookie = Cookies.get(clusterCookieName);

      if (storedIdentity && clusterCookie) {
        log.info('Retrieving identity information from cookie', storedIdentity);
        setIdentity({ identity: storedIdentity });
        return;
      }
      if (!alloy) {
        return;
      }
      try {
        const { identity: alloyIdentity } = await Promise.race<IdentityResponse>([
          alloy<IdentityResponse>('getIdentity'),
          new Promise((_, reject) => {
            setTimeout(() => {
              reject(new Error('Timeout'));
            }, 10000);
          }),
        ]);
        log.info('Retrieving identity information from alloy', alloyIdentity?.ECID);
        setIdentity({ identity: alloyIdentity?.ECID });
      } catch (error) {
        setIdentity({ identity: 'false' });
        log.info('Failed to retrieve identity from Alloy', error);
      }
    })();
  }, [alloy]);

  return { identity };
}
